import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import LoginIcon from '@mui/icons-material/Login';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PeopleIcon from '@mui/icons-material/People';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import TimelineIcon from '@mui/icons-material/Timeline';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { rangeFromQuery } from '../../components/usage/usage-period-filter';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import { UsageKpiCard, UsageKindChip, UsageStatusChip, UsageEmpty, UsageEllipsisCell, UsageOriginChip, UsageRankBar, UsageBlockTitle, fmtDateTime, fmtInt } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Logins e sessões: QUEM ENTROU no sistema, DE ONDE, e se a rede é nova para aquela pessoa.
//   - Sem usuário: visão do contratante — KPIs, redes novas no período, falhas de login (por e-mail e por IP)
//     e as chaves de integração (o usuário dono da chave; user_kind = integration no histórico).
//   - Com usuário: as redes que ele já usou e cada login/logout (com as falhas do e-mail dele e o motivo do logout).
// Rede = IPv4 inteiro ou IPv6 /64 (o final do IPv6 muda sozinho). Rede nova = apareceu no período e o usuário já
// tinha histórico 7 dias antes. A memória de redes é a tabela tb_user_usage_known_network (BackgroundTasks mantém).
// Não confundir com a aba "Acessos a um registro" (quem abriu o chamado X).
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;
const cardStyle = { border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 12 };
const NEW_COLOR = '#c62828';

export default class UsageSessions extends Component {
  constructor(props) {
    super(props);
    const q = new URLSearchParams(props.location?.search || '');
    this.state = {
      filter: { ...rangeFromQuery(q, '7d'), api: '', actionGroup: '', userId: Number(q.get('userId')) || 0, userName: '', ip: props.ip || '' },
      loading: false, loaded: false, available: true,
      summary: null, networks: [], networksCount: 0, networksPage: 1,
      failures: { total: 0, byIp: [], byEmail: [] }, keys: [],
      userNetworks: [], events: [], eventsCount: 0, eventsPage: 1,
      drawerRow: null, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(); }

  componentDidUpdate(prevProps) {
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }));
      return;
    }
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      const q = new URLSearchParams(search);
      this.setState((s) => ({ filter: { ...s.filter, ...rangeFromQuery(q, s.filter.preset || '7d'), userId: Number(q.get('userId')) || 0, userName: '' } }), () => this.load());
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  onFilter(next) {
    if ((next.ip || '') !== (this.state.filter.ip || '') && this.props.onIpChange) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }

  fail(err) { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); }

  load() {
    const f = this.state.filter;
    this.setState({ loading: true, networksPage: 1, eventsPage: 1 });
    const summary = UsageService.sessionsSummary(f);
    if (f.userId) {
      Promise.all([summary, UsageService.sessionsUserNetworks(f), this.eventsQuery(1)])
        .then(([s, nets, ev]) => this.setState({ loading: false, loaded: true, summary: s, available: s.available, userNetworks: nets, events: ev.data, eventsCount: ev.count }))
        .catch((err) => this.fail(err));
      return;
    }
    Promise.all([summary, UsageService.sessionsNewNetworks(f, 'user', 1, PAGE_SIZE), UsageService.authFailures(f, 10), UsageService.sessionsIntegrations(f, 50)])
      .then(([s, nets, failures, keys]) => this.setState({ loading: false, loaded: true, summary: s, available: s.available && nets.available, networks: nets.data, networksCount: nets.count, failures, keys }))
      .catch((err) => this.fail(err));
  }

  // Logins e logouts do usuário: a linha do tempo plana restrita à família auth. O backend inclui as falhas de login
  // feitas com o e-mail dele (senha errada não tem user_id).
  eventsQuery(page) {
    const f = this.state.filter;
    return UsageService.activity({ userId: f.userId, dateStart: f.dateStart, dateEnd: f.dateEnd, page, pageSize: PAGE_SIZE, actionGroup: 'auth', ip: f.ip });
  }

  onNetworksPage(page) {
    this.setState({ loading: true, networksPage: page });
    UsageService.sessionsNewNetworks(this.state.filter, 'user', page, PAGE_SIZE)
      .then((nets) => this.setState({ loading: false, networks: nets.data, networksCount: nets.count }))
      .catch((err) => this.fail(err));
  }

  onEventsPage(page) {
    this.setState({ loading: true, eventsPage: page });
    this.eventsQuery(page).then((ev) => this.setState({ loading: false, events: ev.data, eventsCount: ev.count })).catch((err) => this.fail(err));
  }

  pickUser(userId) { if (!userId) return; this.setState((s) => ({ filter: { ...s.filter, userId, userName: '' } }), () => this.load()); }

  goTimeline(userId, ip, extra) {
    const f = this.state.filter;
    const q = new URLSearchParams();
    if (userId) q.set('userId', userId);
    q.set('start', moment(f.dateStart, 'YYYY/MM/DD HH:mm:ss').format('YYYY-MM-DD'));
    q.set('end', moment(f.dateEnd, 'YYYY/MM/DD HH:mm:ss').format('YYYY-MM-DD'));
    Object.keys(extra || {}).forEach((k) => q.set(k, extra[k]));
    // IP é filtro da ÁREA (index.js): aplicar antes de navegar para a linha do tempo já abrir com ele.
    if (ip && this.props.onIpChange) this.props.onIpChange(ip);
    this.props.history.push(`/${this.props.lang}/usage/user-activity?${q.toString()}`);
  }

  renderKpis() {
    const { intl } = this.props;
    const s = this.state.summary; if (!s) return null;
    return (
      <Grid container spacing={2} style={{ marginBottom: 12 }}>
        <Grid item xs={12} sm={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ses.kpi.ok', 'Logins com sucesso')} value={s.loginsOk} icon={<LoginIcon />} color="#2e7d32" /></Grid>
        <Grid item xs={12} sm={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ses.kpi.failed', 'Falhas de login')} value={s.loginsFailed} icon={<ReportProblemIcon />} color="#F44336" subtitle={this.tr('usage.ses.kpi.failed.sub', 'inclui chamadas do próprio fluxo de login que devolvem erro')} /></Grid>
        <Grid item xs={12} sm={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ses.kpi.users', 'Usuários que entraram')} value={s.distinctUsers} icon={<PeopleIcon />} color="#3F51B4" /></Grid>
        <Grid item xs={12} sm={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ses.kpi.new', 'Redes novas no período')} value={s.newNetworks} icon={<NewReleasesIcon />} color={NEW_COLOR} subtitle={this.tr('usage.ses.kpi.new.sub', 'rede que o usuário nunca tinha usado')} /></Grid>
      </Grid>
    );
  }

  renderNoLogin() {
    return <Tooltip title={this.tr('usage.ses.noLogin.tip', 'A rede teve uso autenticado mas nenhum login: o token foi obtido em outra rede e usado aqui.')}><Chip size="small" label={this.tr('usage.ses.noLogin', 'sem login')} style={{ backgroundColor: NEW_COLOR, color: '#fff', fontWeight: 600, height: 20 }} /></Tooltip>;
  }

  renderNewNetworks() {
    const { intl } = this.props;
    const { networks, networksCount, networksPage } = this.state;
    return (
      <Card style={cardStyle}><CardContent>
        <UsageBlockTitle title={this.tr('usage.ses.new.title', 'Redes novas no período')} subtitle={this.tr('usage.ses.new.subtitle', 'primeira vez que o usuário usou o sistema a partir desta rede (IPv6 comparado pelo /64) · só quem já tinha histórico 7 dias antes')} />
        {!networks.length ? <UsageEmpty intl={intl} compact title={this.tr('usage.ses.new.none', 'Nenhuma rede nova no período')} /> : (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell><TableCell>{this.tr('usage.ses.col.network', 'Rede')}</TableCell><TableCell>{this.tr('usage.col.ip', 'IP')}</TableCell>
                <TableCell>{this.tr('usage.ses.col.firstSeen', 'Primeira vez')}</TableCell><TableCell>{this.tr('usage.ses.col.firstLogin', 'Primeiro login')}</TableCell>
                <TableCell align="right">{this.tr('usage.ses.col.known', 'Redes conhecidas')}</TableCell><TableCell align="right">{this.tr('usage.ses.col.failures', 'Falhas de login do IP no dia')}</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {networks.map((n) => (
                  <TableRow key={`${n.userId}-${n.network}`} hover>
                    <TableCell style={{ cursor: 'pointer', color: '#277afe' }} onClick={() => this.pickUser(n.userId)}><UsageEllipsisCell text={n.userName || `#${n.userId}`} maxWidth={220} /></TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{n.network}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{n.sampleIp}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(n.firstSeen)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{n.firstLoginAt ? fmtDateTime(n.firstLoginAt) : this.renderNoLogin()}</TableCell>
                    <TableCell align="right">{fmtInt(n.knownNetworks)}</TableCell>
                    <TableCell align="right" style={{ color: n.failuresSameDay ? NEW_COLOR : '#888', fontWeight: n.failuresSameDay ? 600 : 400 }}>{fmtInt(n.failuresSameDay)}</TableCell>
                    <TableCell align="right"><Tooltip title={this.tr('usage.ses.goTimeline', 'Ver o que foi feito a partir deste IP')}><Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTimeline(n.userId, n.sampleIp)}>{this.tr('usage.tab.user-activity', 'Linha do tempo')}</Button></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {networksCount > PAGE_SIZE && <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 12 }}><Pagination count={Math.ceil(networksCount / PAGE_SIZE)} page={networksPage} onChange={(e, p) => this.onNetworksPage(p)} /><Typography variant="caption" style={{ color: '#888' }}>{fmtInt(networksCount)}</Typography></Box>}
      </CardContent></Card>
    );
  }

  renderFailures() {
    const { intl } = this.props;
    const { failures } = this.state;
    const sec = (it) => `${fmtInt(it.distinct)} ${this.tr('usage.ses.fail.distinct', 'distintos')}`;
    return (
      <Card style={cardStyle}><CardContent>
        <UsageBlockTitle title={this.tr('usage.blocked.auth.title', 'Falhas de login')} subtitle={`${fmtInt(failures.total)} ${this.tr('usage.ses.fail.subtitle', 'no período · por e-mail tentado e por IP de origem')}`} />
        {failures.total === 0 ? <UsageEmpty intl={intl} compact title={this.tr('usage.blocked.auth.none', 'Nenhuma falha de login no período')} /> : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 4 }}>{this.tr('usage.ses.fail.byEmail', 'E-mails com mais falhas (IPs distintos ao lado)')}</Typography>
              <UsageRankBar intl={intl} items={failures.byEmail} labelKey="key" valueKey="failures" color="#F44336" secondary={sec} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 4 }}>{this.tr('usage.ses.fail.byIp', 'IPs com mais falhas (e-mails distintos ao lado) · clique para ver o IP')}</Typography>
              <UsageRankBar intl={intl} items={failures.byIp} labelKey="key" valueKey="failures" color="#FF9800" secondary={sec} onClick={(it) => this.goTimeline(0, it.key, { actionKind: 'login' })} />
            </Grid>
          </Grid>
        )}
      </CardContent></Card>
    );
  }

  renderKeys() {
    const { intl } = this.props;
    const { keys } = this.state;
    return (
      <Card style={cardStyle}><CardContent>
        <UsageBlockTitle title={this.tr('usage.ses.keys.title', 'Chaves de integração')} subtitle={this.tr('usage.ses.keys.subtitle', 'tokens de longa duração · uma linha por usuário dono da chave · rede nova aqui merece conferência com o cliente')} right={<VpnKeyIcon style={{ color: '#888' }} />} />
        {!keys.length ? <UsageEmpty intl={intl} compact title={this.tr('usage.ses.keys.none', 'Nenhuma chave de integração com uso registrado')} /> : (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.ses.keys.owner', 'Dono da chave')}</TableCell><TableCell align="right">{this.tr('usage.ses.col.networks', 'Redes')}</TableCell><TableCell align="right">{this.tr('usage.ses.col.newNetworks', 'Novas no período')}</TableCell>
                <TableCell>{this.tr('usage.ses.col.lastIp', 'Último IP')}</TableCell><TableCell>{this.tr('usage.ses.col.lastSeen', 'Último uso')}</TableCell>
                <TableCell align="right"><Tooltip title={this.tr('usage.ses.keys.calls.tip', 'Chamadas do USUÁRIO no período, pelo resumo diário, que não separa sessão de integração: se o dono da chave também usa a tela, soma os dois.')}><span>{this.tr('usage.ses.keys.calls', 'Chamadas do usuário')}</span></Tooltip></TableCell>
                <TableCell align="right">{this.tr('usage.col.errors', 'Erros')}</TableCell><TableCell>{this.tr('usage.ses.keys.topRoute', 'Endpoint mais chamado')}</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.userId} hover>
                    <TableCell><UsageEllipsisCell text={k.userName || `#${k.userId}`} maxWidth={220} /></TableCell>
                    <TableCell align="right">{fmtInt(k.networks)}</TableCell>
                    <TableCell align="right" style={{ color: k.newNetworks ? NEW_COLOR : '#888', fontWeight: k.newNetworks ? 700 : 400 }}>{fmtInt(k.newNetworks)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{k.lastIp}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(k.lastSeen)}</TableCell>
                    <TableCell align="right">{fmtInt(k.calls)}</TableCell>
                    <TableCell align="right">{fmtInt(k.errors)}</TableCell>
                    <TableCell><UsageEllipsisCell text={k.topRoute || '—'} maxWidth={260} /></TableCell>
                    <TableCell align="right"><Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTimeline(k.userId, '', { userKind: 'integration' })}>{this.tr('usage.tab.user-activity', 'Linha do tempo')}</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent></Card>
    );
  }

  renderUserNetworks() {
    const { intl } = this.props;
    const { userNetworks, filter } = this.state;
    return (
      <Card style={cardStyle}><CardContent>
        <UsageBlockTitle title={this.tr('usage.ses.user.networks', 'Redes que este usuário já usou')} subtitle={this.tr('usage.ses.user.networks.subtitle', 'toda a memória, não só o período · "nova" = apareceu dentro do período escolhido')} />
        {!userNetworks.length ? <UsageEmpty intl={intl} compact title={this.tr('usage.ses.user.networks.none', 'Nenhuma rede registrada para este usuário')} /> : (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.ses.col.network', 'Rede')}</TableCell><TableCell>{this.tr('usage.ua.userKind', 'Identidade')}</TableCell><TableCell>{this.tr('usage.ses.col.firstSeen', 'Primeira vez')}</TableCell>
                <TableCell>{this.tr('usage.ses.col.lastSeen', 'Último uso')}</TableCell><TableCell>{this.tr('usage.ses.col.firstLogin', 'Primeiro login')}</TableCell><TableCell>{this.tr('usage.ses.col.lastLogin', 'Último login')}</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {userNetworks.map((n) => (
                  <TableRow key={`${n.userKind}-${n.network}`} hover style={{ background: n.isNew ? '#fff5f5' : undefined }}>
                    <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{n.network}{n.isNew && <Chip size="small" label={this.tr('usage.ses.new', 'nova')} style={{ backgroundColor: NEW_COLOR, color: '#fff', fontWeight: 600, height: 20, marginLeft: 8 }} />}</TableCell>
                    <TableCell>{this.tr(`usage.ua.userKind.${n.userKind}`, n.userKind)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(n.firstSeen)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(n.lastSeen)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{n.firstLoginAt ? fmtDateTime(n.firstLoginAt) : (n.userKind === 'integration' ? '—' : this.renderNoLogin())}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{n.lastLoginAt ? fmtDateTime(n.lastLoginAt) : '—'}</TableCell>
                    <TableCell align="right"><Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTimeline(filter.userId, n.sampleIp)}>{this.tr('usage.tab.user-activity', 'Linha do tempo')}</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent></Card>
    );
  }

  renderEvents() {
    const { intl } = this.props;
    const { events, eventsCount, eventsPage } = this.state;
    return (
      <Card style={cardStyle}><CardContent>
        <UsageBlockTitle title={this.tr('usage.ses.user.events', 'Logins e logouts')} subtitle={this.tr('usage.ua.login.hint', 'Inclui as tentativas que falharam com o e-mail deste usuário: senha errada não identifica o usuário, só o e-mail tentado.')} />
        {!events.length ? <UsageEmpty intl={intl} compact title={this.tr('usage.ses.user.events.none', 'Nenhum login ou logout no período')} /> : (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell>{this.tr('usage.col.action', 'Ação')}</TableCell><TableCell>{this.tr('usage.ses.col.result', 'Resultado')}</TableCell>
                <TableCell>{this.tr('usage.col.ip', 'IP')}</TableCell><TableCell>{this.tr('usage.col.origin', 'Origem')}</TableCell><TableCell>{this.tr('usage.col.route', 'Rota')}</TableCell><TableCell>Status</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {events.map((r) => {
                  const failed = Number(r.statusCode) >= 400;
                  const result = r.actionKind === 'logout'
                    ? (r.details && r.details.reason === 'session_expired' ? this.tr('usage.ses.logout.expired', 'sessão expirou') : this.tr('usage.ses.logout.user', 'saiu'))
                    : (failed ? this.tr('usage.ua.login.failed', 'falhou') : this.tr('usage.ua.login.ok', 'ok'));
                  return (
                    <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => this.setState({ drawerRow: r })}>
                      <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt, true)}</TableCell>
                      <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap', fontWeight: 600, color: r.actionKind === 'logout' ? '#666' : (failed ? NEW_COLOR : '#2e7d32') }}>{result}</TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{r.ip}</TableCell>
                      <TableCell><UsageOriginChip intl={intl} row={r} maxWidth={200} /></TableCell>
                      <TableCell><UsageEllipsisCell text={r.route} title={r.url} maxWidth={240} /></TableCell>
                      <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {eventsCount > PAGE_SIZE && <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 12 }}><Pagination count={Math.ceil(eventsCount / PAGE_SIZE)} page={eventsPage} onChange={(e, p) => this.onEventsPage(p)} /><Typography variant="caption" style={{ color: '#888' }}>{fmtInt(eventsCount)}</Typography></Box>}
      </CardContent></Card>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, loading, loaded, available, drawerRow, snack } = this.state;
    return (
      <Box>
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showApi={false} showGroup={false} onApply={this.load} applying={loading} />
        <Box style={{ marginTop: 16 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
          {loaded && !available && <Alert severity="warning" style={{ marginBottom: 12 }}>{this.tr('usage.ses.unavailable', 'A memória de redes conhecidas ainda não existe neste banco (migration 20260921100000). Logins e falhas aparecem; "rede nova" e chaves de integração ficam vazios até ela ser aplicada.')}</Alert>}
          {loaded && this.renderKpis()}
          {loaded && !filter.userId && this.renderNewNetworks()}
          {loaded && !filter.userId && this.renderFailures()}
          {loaded && !filter.userId && this.renderKeys()}
          {loaded && !!filter.userId && this.renderUserNetworks()}
          {loaded && !!filter.userId && this.renderEvents()}
        </Box>
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={() => this.setState({ drawerRow: null })} lang={this.props.lang} history={this.props.history} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
